import React from 'react';
import { Box, TextField, Button, Paper, Divider } from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useMissionBuilder } from '../MissionBuilderContext';
import { createTemplate, updateTemplate } from '../hooks/useMissions';
import { BlockPalette } from './BlockPalette';
import { FlowCanvas } from './FlowCanvas';
import { BlockConfigPanel } from './BlockConfigPanel';

export function FlowBuilder() {
  const {
    missionName, setMissionName,
    missionDescription, setMissionDescription,
    storeId, setStoreId,
    editingTemplate,
    serialize, setView, resetBuilder,
  } = useMissionBuilder();
  const notify = useNotify();
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    const data = serialize();
    if (!data) {
      notify('Mission needs: 1 trigger + 1 mission type + at least 1 step', { type: 'warning' });
      return;
    }
    if (!data.name.trim()) {
      notify('Mission name is required', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, data);
        notify('Mission updated', { type: 'success' });
      } else {
        await createTemplate(data);
        notify('Mission created', { type: 'success' });
      }
      resetBuilder();
      setView('list');
    } catch (e: any) {
      notify(e.message, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    resetBuilder();
    setView('list');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={handleBack} size="small">
          Back
        </Button>
        <TextField
          placeholder="Mission Name"
          size="small"
          value={missionName}
          onChange={(e) => setMissionName(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          placeholder="Store ID (blank = all)"
          size="small"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          sx={{ width: 180 }}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="small"
        >
          {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Save'}
        </Button>
      </Box>

      {/* Description */}
      <TextField
        placeholder="Description (optional)"
        size="small"
        value={missionDescription}
        onChange={(e) => setMissionDescription(e.target.value)}
        fullWidth
        multiline
        maxRows={2}
      />

      {/* 3-Panel Builder */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Left: Palette */}
        <Paper sx={{ width: 220, flexShrink: 0, overflow: 'auto' }} variant="outlined">
          <BlockPalette />
        </Paper>

        {/* Center: Canvas */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FlowCanvas />
        </Box>

        {/* Right: Config */}
        <Paper sx={{ width: 280, flexShrink: 0, overflow: 'auto' }} variant="outlined">
          <BlockConfigPanel />
        </Paper>
      </Box>
    </Box>
  );
}
